import { useState } from 'react';
import {
  Plus,
  Trash2,
  Pencil,
  Play,
  Eye,
  Copy,
  Check,
  AlertTriangle,
  Globe,
  Key,
  Send,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import {
  useWebhookEndpoints,
  useCreateWebhookEndpoint,
  useUpdateWebhookEndpoint,
  useDeleteWebhookEndpoint,
  useWebhookDeliveries,
  useTestWebhookEndpoint,
  useInboundTokens,
  useCreateInboundToken,
  useDeleteInboundToken,
} from '@/hooks/useWebhooks';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Modal,
  ConfirmDialog,
  Input,
  Tabs,
  EmptyState,
  Skeleton,
} from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import { formatDate, formatRelativeTime, truncate } from '@/lib/utils';
import type {
  WebhookEndpoint,
  WebhookDelivery,
  InboundWebhookToken,
} from '@/types';

const AVAILABLE_EVENTS = [
  { value: 'contact.created', label: 'Contact Created' },
  { value: 'contact.updated', label: 'Contact Updated' },
  { value: 'deal.created', label: 'Deal Created' },
  { value: 'deal.stage_changed', label: 'Deal Stage Changed' },
  { value: 'deal.won', label: 'Deal Won' },
  { value: 'deal.closed', label: 'Deal Closed' },
  { value: 'deal.value_changed', label: 'Deal Value Changed' },
  { value: 'form.submitted', label: 'Form Submitted' },
  { value: 'form.ai_reviewed', label: 'Form AI Reviewed' },
  { value: 'proposal.created', label: 'Proposal Created' },
  { value: 'proposal.status_changed', label: 'Proposal Status Changed' },
  { value: 'meeting.recorded', label: 'Meeting Recorded' },
];

export default function WebhookSettings() {
  const [tab, setTab] = useState('outbound');

  return (
    <div className="space-y-6">
      <Tabs
        value={tab}
        onValueChange={setTab}
        items={[
          {
            value: 'outbound',
            label: (
              <>
                <Send className="w-3.5 h-3.5 mr-1.5 inline" />
                Outbound Endpoints
              </>
            ),
            content: <OutboundEndpointsTab />,
          },
          {
            value: 'inbound',
            label: (
              <>
                <Key className="w-3.5 h-3.5 mr-1.5 inline" />
                Inbound Tokens
              </>
            ),
            content: <InboundTokensTab />,
          },
        ]}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────
   Outbound Endpoints Tab
   ───────────────────────────────────────────── */

function OutboundEndpointsTab() {
  const { data: endpoints, isLoading } = useWebhookEndpoints();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<WebhookEndpoint | null>(null);
  const [viewingDeliveries, setViewingDeliveries] = useState<string | null>(null);

  if (isLoading) {
    return <Skeleton className="h-64 w-full rounded-xl" />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Outbound Webhook Endpoints"
          subtitle="Send event notifications to external services when things happen in Solid Sales."
          actions={
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3.5 h-3.5" />
              Add Endpoint
            </Button>
          }
        />

        <CardBody className="space-y-2">
          {endpoints?.length === 0 ? (
            <EmptyState
              icon={<Globe />}
              title="No webhook endpoints configured"
              action={
                <Button size="sm" onClick={() => setShowAdd(true)}>
                  <Plus className="w-3.5 h-3.5" />
                  Add Endpoint
                </Button>
              }
            />
          ) : (
            endpoints?.map((endpoint) => (
              <EndpointRow
                key={endpoint.id}
                endpoint={endpoint}
                onEdit={() => setEditing(endpoint)}
                onViewDeliveries={() =>
                  setViewingDeliveries(
                    viewingDeliveries === endpoint.id ? null : endpoint.id
                  )
                }
                isDeliveriesOpen={viewingDeliveries === endpoint.id}
              />
            ))
          )}
        </CardBody>
      </Card>

      {showAdd && (
        <AddEndpointDialog open={showAdd} onOpenChange={setShowAdd} />
      )}

      {editing && (
        <EditEndpointDialog
          endpoint={editing}
          open={!!editing}
          onOpenChange={(open) => !open && setEditing(null)}
        />
      )}
    </div>
  );
}

function EndpointRow({
  endpoint,
  onEdit,
  onViewDeliveries,
  isDeliveriesOpen,
}: {
  endpoint: WebhookEndpoint;
  onEdit: () => void;
  onViewDeliveries: () => void;
  isDeliveriesOpen: boolean;
}) {
  const updateEndpoint = useUpdateWebhookEndpoint();
  const deleteEndpoint = useDeleteWebhookEndpoint();
  const testEndpoint = useTestWebhookEndpoint();

  const handleToggleActive = async () => {
    await updateEndpoint.mutateAsync({
      id: endpoint.id,
      data: { isActive: !endpoint.isActive },
    });
  };

  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleTest = async () => {
    await testEndpoint.mutateAsync(endpoint.id);
  };

  return (
    <div className="rounded-lg bg-grey-50">
      <div className="flex items-center gap-3 p-3 group">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-secondary">{endpoint.name}</span>
            <Badge variant={endpoint.isActive ? 'success' : 'default'} size="sm">
              {endpoint.isActive ? 'Active' : 'Inactive'}
            </Badge>
            {endpoint.failCount > 0 && (
              <Badge variant="error" size="sm">
                <AlertTriangle className="w-3 h-3 mr-0.5" />
                {endpoint.failCount} failures
              </Badge>
            )}
          </div>
          <p className="text-xs text-grey-500 mt-0.5 font-mono">
            {truncate(endpoint.url, 60)}
          </p>
          <div className="flex flex-wrap gap-1 mt-1.5">
            {endpoint.events.map((event) => (
              <Badge key={event} variant="outline" size="sm">
                {event}
              </Badge>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleToggleActive}
            className="p-1.5 rounded hover:bg-grey-200 text-grey-400 hover:text-grey-600 transition-colors"
            title={endpoint.isActive ? 'Deactivate' : 'Activate'}
          >
            <div
              className={`w-8 h-4 rounded-full transition-colors relative ${
                endpoint.isActive ? 'bg-success' : 'bg-grey-300'
              }`}
            >
              <div
                className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${
                  endpoint.isActive ? 'left-4.5' : 'left-0.5'
                }`}
              />
            </div>
          </button>

          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
            <button
              type="button"
              onClick={handleTest}
              className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-grey-600"
              aria-label="Send test event"
            >
              <Play className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={onEdit}
              className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-grey-600"
              aria-label="Edit endpoint"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={onViewDeliveries}
              className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-grey-600"
              aria-label="View deliveries"
              aria-expanded={isDeliveriesOpen}
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-error"
              aria-label="Delete endpoint"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete endpoint "${endpoint.name}"?`}
        description="This cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={() => deleteEndpoint.mutateAsync(endpoint.id)}
      />

      {isDeliveriesOpen && <DeliveryLog endpointId={endpoint.id} />}
    </div>
  );
}

function DeliveryLog({ endpointId }: { endpointId: string }) {
  const { data: deliveries, isLoading } = useWebhookDeliveries(endpointId);

  if (isLoading) {
    return (
      <div className="px-3 pb-3">
        <div className="animate-pulse h-20 bg-grey-100 rounded-lg" />
      </div>
    );
  }

  if (!deliveries?.length) {
    return (
      <div className="px-3 pb-3">
        <p className="text-xs text-grey-400 text-center py-4">No deliveries yet</p>
      </div>
    );
  }

  return (
    <div className="px-3 pb-3">
      <div className="border border-grey-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-grey-100 text-grey-600">
              <th className="text-left px-3 py-2 font-medium">Event</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Attempts</th>
              <th className="text-left px-3 py-2 font-medium">Time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-grey-100">
            {deliveries.map((delivery) => (
              <DeliveryRow key={delivery.id} delivery={delivery} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DeliveryRow({ delivery }: { delivery: WebhookDelivery }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className="hover:bg-grey-50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-3 py-2">
          <div className="flex items-center gap-1">
            {expanded ? (
              <ChevronDown className="w-3 h-3 text-grey-400" />
            ) : (
              <ChevronRight className="w-3 h-3 text-grey-400" />
            )}
            <span className="font-mono">{delivery.event}</span>
          </div>
        </td>
        <td className="px-3 py-2">
          <Badge variant={delivery.success ? 'success' : 'error'} size="sm">
            {delivery.success ? delivery.statusCode || 'OK' : delivery.statusCode || 'Failed'}
          </Badge>
        </td>
        <td className="px-3 py-2 text-grey-500">{delivery.attempts}</td>
        <td className="px-3 py-2 text-grey-500">
          {delivery.deliveredAt
            ? formatRelativeTime(delivery.deliveredAt)
            : formatRelativeTime(delivery.createdAt)}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={4} className="px-3 py-2 bg-grey-50">
            <pre className="text-[10px] font-mono text-grey-600 overflow-x-auto max-h-40 overflow-y-auto whitespace-pre-wrap">
              {JSON.stringify(delivery.payload, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

function AddEndpointDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [form, setForm] = useState({
    name: '',
    url: '',
    events: [] as string[],
  });
  const createEndpoint = useCreateWebhookEndpoint();

  const toggleEvent = (event: string) => {
    setForm((prev) => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter((e) => e !== event)
        : [...prev.events, event],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createEndpoint.mutateAsync({
      name: form.name,
      url: form.url,
      events: form.events,
    });
    onOpenChange(false);
    setForm({ name: '', url: '', events: [] });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add Webhook Endpoint"
      description="Configure an endpoint to receive event notifications."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-endpoint-form"
            loading={createEndpoint.isPending}
            disabled={form.events.length === 0}
          >
            Add Endpoint
          </Button>
        </>
      }
    >
      <form id="add-endpoint-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          required
          placeholder="e.g. Slack Notifications"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Input
          label="URL"
          required
          type="url"
          placeholder="https://example.com/webhooks"
          value={form.url}
          onChange={(e) => setForm({ ...form, url: e.target.value })}
        />
        <div>
          <label className="label">Events</label>
          <div className="grid grid-cols-2 gap-2 mt-1.5 max-h-48 overflow-y-auto">
            {AVAILABLE_EVENTS.map((event) => (
              <label
                key={event.value}
                className="flex items-center gap-2 text-sm text-grey-700 cursor-pointer hover:text-secondary"
              >
                <input
                  type="checkbox"
                  checked={form.events.includes(event.value)}
                  onChange={() => toggleEvent(event.value)}
                  className="rounded border-grey-300 text-primary focus:ring-primary"
                />
                {event.label}
              </label>
            ))}
          </div>
          {form.events.length === 0 && (
            <p className="text-xs text-grey-400 mt-1">Select at least one event</p>
          )}
        </div>
      </form>
    </Modal>
  );
}

function EditEndpointDialog({
  endpoint,
  open,
  onOpenChange,
}: {
  endpoint: WebhookEndpoint;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [form, setForm] = useState({
    name: endpoint.name,
    url: endpoint.url,
    events: [...endpoint.events],
  });
  const updateEndpoint = useUpdateWebhookEndpoint();

  const toggleEvent = (event: string) => {
    setForm((prev) => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter((e) => e !== event)
        : [...prev.events, event],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateEndpoint.mutateAsync({
      id: endpoint.id,
      data: {
        name: form.name,
        url: form.url,
        events: form.events,
      },
    });
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Webhook Endpoint"
      footer={
        <>
          <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-endpoint-form"
            loading={updateEndpoint.isPending}
            disabled={form.events.length === 0}
          >
            Save Changes
          </Button>
        </>
      }
    >
      <form id="edit-endpoint-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Input
          label="URL"
          required
          type="url"
          value={form.url}
          onChange={(e) => setForm({ ...form, url: e.target.value })}
        />
        <div>
          <label className="label">Events</label>
          <div className="grid grid-cols-2 gap-2 mt-1.5 max-h-48 overflow-y-auto">
            {AVAILABLE_EVENTS.map((event) => (
              <label
                key={event.value}
                className="flex items-center gap-2 text-sm text-grey-700 cursor-pointer hover:text-secondary"
              >
                <input
                  type="checkbox"
                  checked={form.events.includes(event.value)}
                  onChange={() => toggleEvent(event.value)}
                  className="rounded border-grey-300 text-primary focus:ring-primary"
                />
                {event.label}
              </label>
            ))}
          </div>
        </div>

        <div className="rounded-lg bg-grey-50 p-3">
          <label className="label text-xs">Signing Secret</label>
          <div className="flex items-center gap-2 mt-1">
            <code className="text-xs font-mono text-grey-600 bg-grey-100 px-2 py-1 rounded flex-1 overflow-hidden text-ellipsis">
              {endpoint.secret}
            </code>
            <CopyButton text={endpoint.secret} />
          </div>
        </div>

      </form>
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   Inbound Tokens Tab
   ───────────────────────────────────────────── */

function InboundTokensTab() {
  const { data: tokens, isLoading } = useInboundTokens();
  const [showCreate, setShowCreate] = useState(false);

  if (isLoading) {
    return <Skeleton className="h-64 w-full rounded-xl" />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Inbound Webhook Tokens"
          subtitle="Generate tokens for external services to send data into Solid Sales."
          actions={
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="w-3.5 h-3.5" />
              Create Token
            </Button>
          }
        />

        <CardBody className="space-y-2">
          {tokens?.length === 0 ? (
            <EmptyState
              icon={<Key />}
              title="No inbound tokens configured"
              action={
                <Button size="sm" onClick={() => setShowCreate(true)}>
                  <Plus className="w-3.5 h-3.5" />
                  Create Token
                </Button>
              }
            />
          ) : (
            tokens?.map((token) => (
              <InboundTokenRow key={token.id} token={token} />
            ))
          )}
        </CardBody>
      </Card>

      {showCreate && (
        <CreateTokenDialog open={showCreate} onOpenChange={setShowCreate} />
      )}
    </div>
  );
}

function InboundTokenRow({ token }: { token: InboundWebhookToken }) {
  const deleteToken = useDeleteInboundToken();

  const [confirmDelete, setConfirmDelete] = useState(false);

  const maskedToken = token.token.slice(0, 8) + '...' + token.token.slice(-4);

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-grey-50 group">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-secondary">{token.name}</span>
          <Badge variant={token.isActive ? 'success' : 'default'} size="sm">
            {token.isActive ? 'Active' : 'Inactive'}
          </Badge>
          <Badge variant="outline" size="sm">
            {token.source}
          </Badge>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <code className="text-xs font-mono text-grey-500">{maskedToken}</code>
          <CopyButton text={token.token} />
        </div>
        {token.lastUsedAt && (
          <p className="text-xs text-grey-400 mt-0.5">
            Last used {formatRelativeTime(token.lastUsedAt)}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-xs text-grey-400">{formatDate(token.createdAt)}</span>
        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-error"
            aria-label="Delete token"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete token "${token.name}"?`}
        description="External services using this token will stop working."
        confirmLabel="Delete"
        destructive
        onConfirm={() => deleteToken.mutateAsync(token.id)}
      />
    </div>
  );
}

function CreateTokenDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [form, setForm] = useState({ name: '', source: '' });
  const createToken = useCreateInboundToken();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createToken.mutateAsync({
      name: form.name,
      source: form.source,
    });
    onOpenChange(false);
    setForm({ name: '', source: '' });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Create Inbound Token"
      description="Generate a token for an external service to send webhooks to Solid Sales."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="create-token-form" loading={createToken.isPending}>
            Create Token
          </Button>
        </>
      }
    >
      <form id="create-token-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          required
          placeholder="e.g. WordPress Contact Form"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Input
          label="Source"
          required
          placeholder="e.g. WordPress, Zapier, Fathom"
          value={form.source}
          onChange={(e) => setForm({ ...form, source: e.target.value })}
        />
      </form>
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   Shared Components
   ───────────────────────────────────────────── */

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-grey-600 transition-colors"
      aria-label={copied ? 'Copied' : 'Copy to clipboard'}
    >
      {copied ? (
        <Check className="w-3.5 h-3.5 text-success" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
    </button>
  );
}
